import { useEffect, useState } from "react";
import api from "../lib/api";
import {
  Plus, Edit3, LogIn, Scissors, CheckCircle2, CreditCard, XCircle,
  AlertCircle, Calendar as CalIcon, Loader2,
} from "lucide-react";

const EVENT_META = {
  booked:       { label: "Booked",        icon: Plus,         color: "text-emerald-600", bg: "bg-emerald-50",   ring: "ring-emerald-100" },
  rescheduled:  { label: "Rescheduled",   icon: CalIcon,      color: "text-amber-600",   bg: "bg-amber-50",     ring: "ring-amber-100" },
  checked_in:   { label: "Checked in",    icon: LogIn,        color: "text-sky-600",     bg: "bg-sky-50",       ring: "ring-sky-100" },
  in_service:   { label: "In service",    icon: Scissors,     color: "text-violet-600",  bg: "bg-violet-50",    ring: "ring-violet-100" },
  completed:    { label: "Completed",     icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50",   ring: "ring-emerald-100" },
  paid:         { label: "Paid",          icon: CreditCard,   color: "text-rose-600",    bg: "bg-rose-50",      ring: "ring-rose-100" },
  checked_out:  { label: "Checked out",   icon: CreditCard,   color: "text-rose-600",    bg: "bg-rose-50",      ring: "ring-rose-100" },
  cancelled:    { label: "Cancelled",     icon: XCircle,      color: "text-slate-500",   bg: "bg-slate-100",    ring: "ring-slate-200" },
  no_show:      { label: "No-show",       icon: AlertCircle,  color: "text-rose-500",    bg: "bg-rose-50",      ring: "ring-rose-100" },
  edited:       { label: "Edited",        icon: Edit3,        color: "text-slate-500",   bg: "bg-slate-100",    ring: "ring-slate-200" },
};

const SOURCE_META = {
  online: { label: "Online", className: "bg-sky-100 text-sky-700" },
  staff:  { label: "Staff",  className: "bg-slate-100 text-slate-600" },
  kiosk:  { label: "Kiosk",  className: "bg-violet-100 text-violet-700" },
  system: { label: "System", className: "bg-amber-100 text-amber-700" },
};

const fmtAt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString([], {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
};

/**
 * Activity / audit-log tab for an appointment.
 * Self-contained — fetches `/appointments/{id}/audit-log` on mount and
 * re-fetches when refreshKey changes (so callers can prompt a reload after
 * a save).
 */
export default function AppointmentAuditLog({ appointmentId, refreshKey = 0 }) {
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!appointmentId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get(`/appointments/${appointmentId}/audit-log`)
      .then(r => { if (!cancelled) setEvents(r.data?.events || []); })
      .catch(err => {
        if (!cancelled) {
          if (err.response?.status === 403) setError("You don't have access to view this audit log.");
          else if (err.response?.status === 404) setError("Audit log not found.");
          else setError("Could not load audit log.");
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [appointmentId, refreshKey]);

  if (loading) {
    return (
      <div data-testid="audit-loading" className="flex items-center justify-center py-8 text-slate-400">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="audit-error" className="text-center py-8 text-sm text-slate-500">{error}</div>
    );
  }

  if (events.length === 0) {
    return (
      <div data-testid="audit-empty" className="text-center py-8">
        <CalIcon className="w-8 h-8 text-slate-200 mx-auto mb-2" strokeWidth={1.5} />
        <p className="text-sm text-slate-400 font-['Manrope']">No activity recorded yet.</p>
        <p className="text-xs text-slate-300 font-['Manrope'] mt-0.5">Status changes after now will appear here.</p>
      </div>
    );
  }

  return (
    <div data-testid="audit-log-list" className="relative">
      {/* Vertical timeline rail */}
      <div className="absolute left-4 top-2 bottom-2 w-px bg-slate-200" aria-hidden="true" />
      <ol className="space-y-3">
        {events.map((e) => {
          const meta = EVENT_META[e.event_type] || EVENT_META.edited;
          const src = SOURCE_META[e.source] || { label: e.source || "—", className: "bg-slate-100 text-slate-600" };
          const Icon = meta.icon;
          const md = e.metadata || {};
          return (
            <li
              key={e.event_id}
              data-testid={`audit-event-${e.event_id}`}
              className="relative pl-10">
              <div className={`absolute left-0 top-1 w-8 h-8 rounded-full ${meta.bg} ring-4 ring-white flex items-center justify-center`}>
                <Icon className={`w-4 h-4 ${meta.color}`} strokeWidth={2} />
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-3.5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <p className={`text-sm font-semibold font-['Manrope'] ${meta.color}`}>{meta.label}</p>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${src.className}`}>
                      {src.label}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-400 font-['Manrope'] tabular-nums">{fmtAt(e.created_at)}</p>
                </div>
                <p className="text-xs text-slate-500 font-['Manrope'] mt-1">
                  by <span className="font-semibold text-slate-700">{e.actor_name || "—"}</span>
                  {e.actor_role && <span className="text-slate-400"> · {e.actor_role}</span>}
                </p>
                {/* Status transition */}
                {(md.old_status || md.new_status) && (
                  <p className="text-[11px] text-slate-500 mt-1.5 font-['Manrope']">
                    <span className="text-slate-400">{md.old_status || "—"}</span>
                    <span className="mx-1.5 text-slate-300">→</span>
                    <span className="font-semibold text-slate-700">{md.new_status || "—"}</span>
                  </p>
                )}
                {/* Reschedule details */}
                {e.event_type === "rescheduled" && md.old && md.new && (
                  <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                    <div className="bg-slate-50 rounded p-2">
                      <p className="text-slate-400 font-bold uppercase tracking-wider mb-0.5">From</p>
                      <p className="text-slate-600">{fmtAt(md.old.start_time)}</p>
                      {md.old.provider_name && <p className="text-slate-500">{md.old.provider_name}</p>}
                    </div>
                    <div className="bg-emerald-50 rounded p-2">
                      <p className="text-emerald-600 font-bold uppercase tracking-wider mb-0.5">To</p>
                      <p className="text-slate-700">{fmtAt(md.new.start_time)}</p>
                      {md.new.provider_name && <p className="text-slate-500">{md.new.provider_name}</p>}
                    </div>
                  </div>
                )}
                {/* Payment details */}
                {(e.event_type === "paid" || e.event_type === "checked_out") && md.total !== undefined && (
                  <p className="text-[11px] text-slate-600 mt-1.5 font-['Manrope']">
                    Total <span className="font-semibold text-slate-800 tabular-nums">${Number(md.total).toFixed(2)}</span>
                    {md.tip > 0 && <> · Tip <span className="font-semibold tabular-nums">${Number(md.tip).toFixed(2)}</span></>}
                    {md.payment_method && <> · <span className="capitalize">{md.payment_method}</span></>}
                  </p>
                )}
                {/* Generic edit */}
                {e.event_type === "edited" && Array.isArray(md.changed_fields) && md.changed_fields.length > 0 && (
                  <p className="text-[11px] text-slate-500 mt-1.5 font-['Manrope']">
                    Changed: <span className="font-semibold text-slate-600">{md.changed_fields.join(", ")}</span>
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
